import { useState, useCallback } from "react";
import {
  Link,
  CheckCircle,
  XCircle,
  AlertCircle,
  Settings,
  Info,
} from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { Button } from "@cieda/djayma-ui";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type IntegrationStatus = "connected" | "disconnected" | "error";

interface Integration {
  readonly id: string;
  readonly name: string;
  readonly category: string;
  readonly description: string;
  readonly status: IntegrationStatus;
  readonly last_sync: string | null;
  readonly config_fields: readonly ConfigField[];
  readonly color: string;
  readonly initials: string;
}

interface ConfigField {
  readonly key: string;
  readonly label: string;
  readonly type: "text" | "password" | "url";
  readonly placeholder: string;
  readonly required: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<
  IntegrationStatus,
  { readonly label: string; readonly icon: typeof CheckCircle; readonly color: string }
> = {
  connected: {
    label: "Connecté",
    icon: CheckCircle,
    color: "text-emerald-600",
  },
  disconnected: {
    label: "Déconnecté",
    icon: XCircle,
    color: "text-stone-400",
  },
  error: {
    label: "Erreur",
    icon: AlertCircle,
    color: "text-red-600",
  },
};

const INTEGRATIONS: readonly Integration[] = [
  {
    id: "wave",
    name: "Wave Money",
    category: "Paiements",
    description: "Paiements mobile money via Wave",
    status: "connected",
    last_sync: "2026-03-24T08:00:00Z",
    color: "bg-blue-500",
    initials: "WV",
    config_fields: [
      { key: "api_key", label: "Clé API Wave", type: "password", placeholder: "wv_live_…", required: true },
      { key: "webhook_url", label: "URL Webhook", type: "url", placeholder: "https://…/webhook/wave", required: false },
    ],
  },
  {
    id: "orange_money",
    name: "Orange Money",
    category: "Paiements",
    description: "Paiements mobile money via Orange",
    status: "connected",
    last_sync: "2026-03-24T07:30:00Z",
    color: "bg-orange-500",
    initials: "OM",
    config_fields: [
      { key: "api_key", label: "Clé API Orange", type: "password", placeholder: "om_live_…", required: true },
      { key: "merchant_id", label: "ID Marchand", type: "text", placeholder: "DJAYMA001", required: true },
      { key: "webhook_url", label: "URL Webhook", type: "url", placeholder: "https://…/webhook/orange", required: false },
    ],
  },
  {
    id: "free_money",
    name: "Free Money",
    category: "Paiements",
    description: "Paiements mobile money via Free",
    status: "disconnected",
    last_sync: null,
    color: "bg-red-500",
    initials: "FM",
    config_fields: [
      { key: "api_key", label: "Clé API Free", type: "password", placeholder: "fm_live_…", required: true },
      { key: "webhook_url", label: "URL Webhook", type: "url", placeholder: "https://…/webhook/free", required: false },
    ],
  },
  {
    id: "yango",
    name: "Yango",
    category: "Livraison",
    description: "Livraison last-mile via Yango",
    status: "connected",
    last_sync: "2026-03-24T08:15:00Z",
    color: "bg-yellow-500",
    initials: "YG",
    config_fields: [
      { key: "api_key", label: "Clé API Yango", type: "password", placeholder: "yng_…", required: true },
      { key: "zone_id", label: "Zone ID", type: "text", placeholder: "dakar-zone-1", required: true },
      { key: "webhook_url", label: "URL Webhook", type: "url", placeholder: "https://…/webhook/yango", required: false },
    ],
  },
  {
    id: "resend",
    name: "Resend",
    category: "Email",
    description: "Envoi transactionnel d'e-mails",
    status: "connected",
    last_sync: "2026-03-24T08:00:00Z",
    color: "bg-stone-900",
    initials: "RS",
    config_fields: [
      { key: "api_key", label: "Clé API Resend", type: "password", placeholder: "re_…", required: true },
      { key: "from_email", label: "E-mail expéditeur", type: "text", placeholder: "no-reply@djayma.com", required: true },
    ],
  },
  {
    id: "twilio",
    name: "Twilio / SMS",
    category: "SMS",
    description: "Envoi de SMS transactionnels",
    status: "error",
    last_sync: "2026-03-23T14:00:00Z",
    color: "bg-red-600",
    initials: "TW",
    config_fields: [
      { key: "account_sid", label: "Account SID", type: "text", placeholder: "AC…", required: true },
      { key: "auth_token", label: "Auth Token", type: "password", placeholder: "…", required: true },
      { key: "from_number", label: "Numéro émetteur", type: "text", placeholder: "+221…", required: true },
    ],
  },
  {
    id: "whatsapp",
    name: "WhatsApp Business",
    category: "Messagerie",
    description: "Notifications via WhatsApp Business API",
    status: "disconnected",
    last_sync: null,
    color: "bg-emerald-500",
    initials: "WA",
    config_fields: [
      { key: "access_token", label: "Token d'accès", type: "password", placeholder: "EAAJ…", required: true },
      { key: "phone_number_id", label: "ID Numéro de téléphone", type: "text", placeholder: "123456789", required: true },
      { key: "verify_token", label: "Token de vérification", type: "text", placeholder: "…", required: false },
    ],
  },
  {
    id: "supabase",
    name: "Supabase",
    category: "Base de données",
    description: "PostgreSQL, Auth et Stockage",
    status: "connected",
    last_sync: "2026-03-24T08:00:00Z",
    color: "bg-emerald-600",
    initials: "SB",
    config_fields: [
      { key: "project_url", label: "URL du projet", type: "url", placeholder: "https://…supabase.co", required: true },
      { key: "service_key", label: "Service Role Key", type: "password", placeholder: "eyJ…", required: true },
    ],
  },
  {
    id: "vercel",
    name: "Vercel",
    category: "Déploiement",
    description: "Hébergement et variables d'environnement",
    status: "connected",
    last_sync: "2026-03-24T08:00:00Z",
    color: "bg-stone-900",
    initials: "VC",
    config_fields: [
      { key: "team_id", label: "Team ID", type: "text", placeholder: "team_…", required: true },
      { key: "project_id", label: "Project ID", type: "text", placeholder: "prj_…", required: true },
    ],
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(d: string | null): string {
  if (!d) return "Jamais";
  return new Date(d).toLocaleDateString("fr-SN");
}

// ---------------------------------------------------------------------------
// Config Modal
// ---------------------------------------------------------------------------

interface ConfigModalProps {
  readonly integration: Integration | null;
  readonly onClose: () => void;
}

function ConfigModal({ integration, onClose }: ConfigModalProps) {
  const [values, setValues] = useState<Record<string, string>>({});

  const handleChange = useCallback((key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  if (!integration) return null;

  return (
    <AdminModal
      open={Boolean(integration)}
      onClose={onClose}
      title={`Configurer : ${integration.name}`}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={onClose}>Enregistrer</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-lg border border-stone-200 bg-stone-50 px-4 py-3">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-stone-500" />
          <p className="text-xs text-stone-600">
            Les configurations sensibles sont stockées dans les variables
            d'environnement Vercel. Ne partagez jamais ces valeurs.
          </p>
        </div>

        {integration.config_fields.map((field) => (
          <div key={field.key}>
            <label className="mb-1 block text-sm font-medium text-stone-700">
              {field.label}
              {field.required && <span className="ml-1 text-red-500">*</span>}
            </label>
            <input
              type={field.type}
              value={values[field.key] ?? ""}
              onChange={(e) => handleChange(field.key, e.target.value)}
              placeholder={field.placeholder}
              className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
          </div>
        ))}
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Integration Card
// ---------------------------------------------------------------------------

interface IntegrationCardProps {
  readonly integration: Integration;
  readonly onConfigure: (integration: Integration) => void;
}

function IntegrationCard({ integration, onConfigure }: IntegrationCardProps) {
  const statusConfig = STATUS_CONFIG[integration.status];
  const StatusIcon = statusConfig.icon;

  return (
    <div className="rounded-lg border border-stone-200 bg-white p-4 transition-shadow hover:shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-bold text-white ${integration.color}`}
          >
            {integration.initials}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-stone-900">{integration.name}</h3>
            <p className="text-xs text-stone-500">{integration.category}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <StatusIcon className={`h-4 w-4 ${statusConfig.color}`} />
          <span className={`text-xs font-medium ${statusConfig.color}`}>
            {statusConfig.label}
          </span>
        </div>
      </div>

      <p className="mt-3 text-xs text-stone-500">{integration.description}</p>

      <div className="mt-3 flex items-center justify-between">
        <p className="text-xs text-stone-400">
          Sync : {formatDate(integration.last_sync)}
        </p>
        <button
          onClick={() => onConfigure(integration)}
          className="flex items-center gap-1 rounded-md border border-stone-200 px-2.5 py-1.5 text-xs font-medium text-stone-600 hover:bg-stone-50"
        >
          <Settings className="h-3.5 w-3.5" />
          Configurer
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Integrations() {
  const [configuringIntegration, setConfiguringIntegration] =
    useState<Integration | null>(null);

  const categories = Array.from(new Set(INTEGRATIONS.map((i) => i.category)));

  const connectedCount = INTEGRATIONS.filter((i) => i.status === "connected").length;
  const errorCount = INTEGRATIONS.filter((i) => i.status === "error").length;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
            <Link className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">
              Intégrations
            </h1>
            <p className="text-sm text-stone-500">
              Services externes connectés à la plateforme
            </p>
          </div>
        </div>

        {/* Summary */}
        <div className="flex items-center gap-4 rounded-lg border border-stone-200 bg-white px-5 py-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-emerald-600" />
            <span className="text-sm font-medium text-stone-900">
              {connectedCount} connectée{connectedCount !== 1 ? "s" : ""}
            </span>
          </div>
          {errorCount > 0 && (
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <span className="text-sm font-medium text-red-700">
                {errorCount} en erreur
              </span>
            </div>
          )}
          <div className="ml-auto">
            <div className="flex items-start gap-2 rounded border border-stone-200 bg-stone-50 px-3 py-1.5">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400" />
              <p className="text-xs text-stone-500">
                Les configurations sensibles sont stockées dans les variables
                d'environnement Vercel.
              </p>
            </div>
          </div>
        </div>

        {/* Cards by category */}
        {categories.map((category) => (
          <div key={category}>
            <h2 className="mb-3 text-sm font-semibold text-stone-700">{category}</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {INTEGRATIONS.filter((i) => i.category === category).map((integration) => (
                <IntegrationCard
                  key={integration.id}
                  integration={integration}
                  onConfigure={setConfiguringIntegration}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <ConfigModal
        integration={configuringIntegration}
        onClose={() => setConfiguringIntegration(null)}
      />
    </AdminLayout>
  );
}
